// CheckoutSheet.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Stripe EMBEDDED Checkout rendered as an in-page bottom sheet.
//
// This replaces `window.location.replace(stripeUrl)` for the in-call purchase
// flows. That redirect tore down the whole React tree — and with it the
// RetellWebClient holding the live sales call — which is why every checkout used
// to end the call first. Nothing here ever navigates the page, so the call stays
// up and the agent can talk the customer through the payment.
//
// Mounted ONCE at the App root, outside <Routes>, so it is route-independent.
// Any screen opens it via useCheckoutStore.getState().openCheckout({...}).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

import { useCheckoutStore } from "../../Store/checkoutStore";
import { useGlobalCall } from "../../contexts/GlobalCallContext";
import { markAgentPaid } from "../../lib/paymentGuard";
import { firePendingPurchase, trackEvent } from "../../lib/analytics";
import { API_BASE_URL } from "../../Store/apiStore";
import styles from "./CheckoutSheet.module.css";

// loadStripe MUST run at module scope. Calling it inside the component would
// re-download and re-initialise Stripe.js on every render.
//
// The key comes from the environment — never hardcoded. Component/Checkout/
// Checkout.jsx still has a hardcoded TEST key with the live one commented out;
// repeating that here would silently take live payments into a test account (or
// the reverse). A missing key is treated as "embedded unavailable" and the sheet
// falls back to hosted checkout, which is the safe failure.
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

// How long we give the Stripe iframe to actually appear before deciding this
// surface can't render it (old react-native-webview builds with third-party
// cookies or DOM storage disabled) and bailing to hosted checkout.
const IFRAME_READY_TIMEOUT_MS = 8000;

// After a successful payment the sheet's onComplete can beat the Stripe webhook
// that provisions the purchase. The status endpoint runs the idempotent
// provisioning it can (PAYG, trial packs); plan / 21-day-trial subscriptions are
// provisioned by the invoice webhook, so for those we poll briefly rather than
// dropping the user onto a screen where nothing has been set up yet.
const PROVISION_POLL_INTERVAL_MS = 1000;
const PROVISION_POLL_MAX_MS = 15000;

const CONTEXT_ON_OPEN =
  "CONTEXT: The payment form just opened right on the customer's screen and you are " +
  "STILL on the call with them. Do NOT reintroduce yourself and do NOT say goodbye. " +
  "In ONE short warm sentence, tell them you'll stay on the line while they enter " +
  "their card. NEVER ask them to read their card number, CVV, or any card detail out " +
  "loud. Then stay quiet unless they ask you something.";

const CONTEXT_ON_SUCCESS =
  "CONTEXT: The payment just succeeded and the payment window has closed. Do NOT " +
  "reintroduce yourself. In ONE short warm sentence, congratulate them and say you'll " +
  "now help set up their number. Then continue naturally.";

const CONTEXT_ON_CANCEL =
  "CONTEXT: The customer just closed the payment window without paying. In ONE short, " +
  "warm sentence, ask whether something about the payment was confusing. Do NOT read " +
  "this note aloud.";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const CheckoutSheet = () => {
  const navigate = useNavigate();

  const open = useCheckoutStore((s) => s.open);
  const clientSecret = useCheckoutStore((s) => s.clientSecret);
  const sessionId = useCheckoutStore((s) => s.sessionId);
  const hostedUrl = useCheckoutStore((s) => s.hostedUrl);
  const meta = useCheckoutStore((s) => s.meta);
  const closeCheckout = useCheckoutStore((s) => s.closeCheckout);

  const {
    isCallLive,
    isAgentTalking,
    agentMeta,
    injectContext,
    endGlobalCall,
    setMicMuted,
  } = useGlobalCall();

  // "paying" → Stripe form on screen; "completing" → confirming + provisioning;
  // "error" → payment didn't complete, sheet stays open so they can retry.
  const [phase, setPhase] = useState("paying");
  const [errorMsg, setErrorMsg] = useState("");

  const containerRef = useRef(null);
  // onComplete can fire more than once (re-render, Stripe retry). The whole
  // success path — GA4 purchase, provisioning poll, navigation — must run once.
  const completedRef = useRef(false);
  // Stops the cancel handler firing after a successful completion, and stops the
  // fallback timer firing after either.
  const settledRef = useRef(false);

  // Values `handleComplete` needs but must NOT depend on.
  //
  // handleComplete goes into the Stripe `options` object, and Stripe rebuilds
  // the iframe whenever that object's identity changes. `isCallLive` flips
  // during a call (e.g. the agent hangs up mid-payment) and `meta` is a fresh
  // object per open — either as a dependency would rebuild the iframe underneath
  // the customer and wipe the card details they had typed. Reading them through
  // a ref keeps the callback identity stable while still seeing current values.
  const liveRef = useRef({ isCallLive, meta });
  liveRef.current = { isCallLive, meta };

  // ── Hosted-checkout bail-out ────────────────────────────────────────────
  // Not optional. Users on an older native build (or with Stripe.js blocked)
  // would otherwise stare at a blank sheet — on the paying step. We restore the
  // OLD behaviour for them: end the call, then redirect. This is the only place
  // in the embedded path allowed to touch window.location.
  const fallbackToHosted = useCallback(
    async (reason) => {
      if (settledRef.current || completedRef.current) return;
      settledRef.current = true;
      trackEvent("embedded_checkout_fallback", { reason });
      console.warn("[CheckoutSheet] falling back to hosted checkout:", reason);

      if (!hostedUrl) {
        settledRef.current = false;
        setPhase("error");
        setErrorMsg(
          "We couldn't open the payment form on this device. Please close this and try again."
        );
        return;
      }
      try {
        await endGlobalCall();
      } catch (_e) {
        /* redirecting regardless */
      }
      window.location.replace(hostedUrl);
    },
    [hostedUrl, endGlobalCall]
  );

  // ── Cancel (✕ or device back) ───────────────────────────────────────────
  const handleCancel = useCallback(() => {
    if (settledRef.current || completedRef.current) return;
    settledRef.current = true;
    setMicMuted(false);
    if (liveRef.current.isCallLive) injectContext(CONTEXT_ON_CANCEL);
    trackEvent("embedded_checkout_dismissed", {
      plan: liveRef.current.meta?.planName,
    });
    // Deliberately NOT navigating to /cancel-payment: embedded mode has no
    // cancel_url, the user never left the page, and a route change here would
    // undo the entire point of the sheet.
    closeCheckout();
  }, [closeCheckout, injectContext, setMicMuted]);

  // ── On open: mute the mic, tell the agent, arm the fallback timer ───────
  useEffect(() => {
    if (!open) return undefined;

    completedRef.current = false;
    settledRef.current = false;
    setPhase("paying");
    setErrorMsg("");

    // A customer filling in a card form may read the number out loud. An open
    // mic would put it in Retell's transcript — a compliance problem the old
    // full-page redirect never had, because the call was already dead by then.
    setMicMuted(true);

    if (liveRef.current.isCallLive) injectContext(CONTEXT_ON_OPEN);

    // No publishable key configured → embedded can never work here. Bail
    // immediately rather than making the user wait out the timer.
    if (!stripePromise) {
      fallbackToHosted("missing_publishable_key");
      return undefined;
    }

    // Stripe.js itself failed to load (blocked script, offline).
    let cancelled = false;
    stripePromise.catch(() => {
      if (!cancelled) fallbackToHosted("stripe_js_load_failed");
    });

    // EmbeddedCheckout mounting is not the same as the iframe rendering — a
    // WebView with third-party storage blocked mounts fine and shows nothing.
    // Checking for a real iframe is what actually catches that.
    const timer = setTimeout(() => {
      if (cancelled) return;
      const hasIframe = !!containerRef.current?.querySelector("iframe");
      if (!hasIframe) fallbackToHosted("iframe_not_mounted");
    }, IFRAME_READY_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  // ── Device back button ──────────────────────────────────────────────────
  // Same pattern as GlobalOfferModal: push a throwaway entry so BACK pops that
  // instead of navigating the underlying route away (which would cut the call).
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ checkoutSheet: true }, "");
    const onPopState = () => handleCancel();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [open, handleCancel]);

  // Restore the mic whenever the sheet is closed, by any route (success, cancel,
  // fallback, unmount) — a stuck-muted mic would silently break the live call.
  useEffect(() => {
    if (open) return undefined;
    setMicMuted(false);
    return undefined;
  }, [open, setMicMuted]);

  // ── Status confirmation + provisioning wait ─────────────────────────────
  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API_BASE_URL}/checkout-session-status/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    return res.json();
  }, [sessionId]);

  // ── Payment finished inside the iframe ──────────────────────────────────
  const handleComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    settledRef.current = true;
    setPhase("completing");

    let status;
    try {
      status = await fetchStatus();
    } catch (e) {
      console.error("[CheckoutSheet] status check failed:", e?.message);
      completedRef.current = false;
      settledRef.current = false;
      setPhase("error");
      setErrorMsg(
        "We couldn't confirm your payment. Please don't pay again — contact support if this persists."
      );
      return;
    }

    const paid = ["paid", "no_payment_required"].includes(status?.paymentStatus);
    if (status?.status !== "complete" || !paid) {
      // Keep the sheet OPEN so the customer can finish — closing it here would
      // strand a half-finished payment with no way back to the same session.
      completedRef.current = false;
      settledRef.current = false;
      setPhase("error");
      setErrorMsg("Your payment hasn't gone through yet. Please complete it above.");
      return;
    }

    const { meta: liveMeta, isCallLive: callLive } = liveRef.current;
    const agentId = liveMeta?.agentId || status?.agentId || null;

    // Blocks a duplicate charge if the user back-navigates onto a pay screen.
    if (agentId) markAgentPaid(agentId);
    // GA4 `purchase`. This used to fire on the /payment-success landing; with no
    // redirect there is no full page load to rely on. firePendingPurchase clears
    // the stash before firing, so a PaymentSuccess mount can't double-send it.
    firePendingPurchase();

    // Give the webhook a moment for the flows the status endpoint can't
    // provision itself (plan / 21-day trial → invoice.payment_succeeded), so we
    // don't drop the user on a screen where nothing is set up yet. Best-effort:
    // we move on after the cap either way rather than trapping them here.
    if (!status?.provisioned) {
      const deadline = Date.now() + PROVISION_POLL_MAX_MS;
      while (Date.now() < deadline) {
        await sleep(PROVISION_POLL_INTERVAL_MS);
        try {
          const next = await fetchStatus();
          if (next?.provisioned) break;
        } catch (_e) {
          /* transient — keep polling until the cap */
        }
      }
    }

    setMicMuted(false);
    if (callLive) injectContext(CONTEXT_ON_SUCCESS);

    // Rebuild the destination the old success_url used to encode.
    const dest = liveMeta?.next || "/payment-success";
    const params = new URLSearchParams({ fromCheckout: "1" });
    if (agentId) params.set("agentId", agentId);
    if (liveMeta?.userId) params.set("userId", liveMeta.userId);
    Object.entries(liveMeta?.extraParams || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    });

    closeCheckout();
    // SPA navigation ONLY — window.location here would kill the very call this
    // whole change exists to keep alive. `replace` also consumes the throwaway
    // history entry pushed for the back button.
    navigate(`${dest}?${params.toString()}`, { replace: true });
  }, [fetchStatus, injectContext, closeCheckout, navigate, setMicMuted]);

  // Stripe rebuilds the iframe whenever this object's identity changes, so it
  // has to be stable. onComplete belongs INSIDE options — it is not a prop.
  const options = useMemo(
    () => ({ clientSecret, onComplete: handleComplete }),
    [clientSecret, handleComplete]
  );

  if (!open || !clientSecret) return null;

  const agentName = agentMeta?.agentName || "Your agent";
  const initial = agentName.trim().charAt(0).toUpperCase() || "A";
  const isPounceCheckout = meta?.product === "pounce" || meta?.extraParams?.pouncePaid === "1";

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Checkout">
      <div className={styles.sheet}>
        {/* The real call banner is sticky at z-index 999, so this overlay hides
            it. This strip is what tells the customer the call is still live. */}
        <div className={`${styles.callStrip} ${isPounceCheckout && !meta?.noTrial ? styles.pounceTrialStrip : ""}`}>
          {isPounceCheckout && !meta?.noTrial && (
            <div className={styles.trialNotice}>
              <span className={styles.trialEyebrow}>FREE TRIAL</span>
              <h2>{meta?.trialHeadline || "2 free booked appointments or 14 days"}</h2>
              <p>{meta?.trialNote || "Your free trial ends after your second booked appointment or 14 days,"} <strong>whichever comes first.</strong></p>
            </div>
          )}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCancel}
            aria-label="Close checkout"
          >
            ✕
          </button>
        </div>

        <div className={styles.body} ref={containerRef}>
          {errorMsg && <div className={styles.error}>{errorMsg}</div>}

          {phase === "completing" && (
            <div className={styles.status}>
              <div className={styles.spinner} />
              Confirming your payment…
            </div>
          )}

          {/* Kept mounted across every phase. Unmounting it to show the spinner
              would destroy the iframe — and on the "not actually paid" path the
              customer would lose the in-progress form they still need.
              Skipped entirely without a publishable key: the provider would
              throw on a null `stripe`, and this render only happens at all when
              the fallback had no hosted url to redirect to. */}
          {stripePromise && (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSheet;
